import { createAsyncThunk, createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import authAxios from "utils/authAxios";

// Thunk for fetching all trades (primarily used for active trades)
export const fetchTrades = createAsyncThunk(
  'trade/fetchTrades', 
  async ({strategyId, status, refresh = false}) => {
    // Build query params
    const params = new URLSearchParams();
    if (status) {
      params.append('status', status);
    }
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return authAxios.get(`/trade/strategy/${strategyId}/${queryString}`).then(response => response.data);
  }
);

// Thunk for fetching paginated closed trades
export const fetchClosedTrades = createAsyncThunk(
  'trade/fetchClosedTrades',
  async ({strategyId, page = 1, pageSize = 10}) => {
    const params = new URLSearchParams({
      status: 'CLOSED',
      page,
      page_size: pageSize
    });
    
    const url = `/trade/strategy/${strategyId}/?${params.toString()}`;
    return authAxios.get(url).then(response => {
      return response.data;
    });
  }
);

export const tradesAdapter = createEntityAdapter();

// Slice
export const tradeSlice = createSlice({
  name: 'trade',
  initialState: tradesAdapter.getInitialState({
    loading: false,
    closedTradesLoading: false,
    error: null,
    pagination: {
      count: 0,
      next: null,
      previous: null,
      page: 1,
      pageSize: 10,
      totalPages: 1
    }
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrades.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrades.fulfilled, (state, action) => {
        // Only manage active trades - don't replace the entire state
        const payload = Array.isArray(action.payload) ? action.payload : action.payload.results || [];
        
        // Get the status from meta arg
        const fetchedStatus = action.meta.arg.status;
        
        if (fetchedStatus === 'ACTIVE') {
          // First, remove all ACTIVE trades from store
          const currentEntities = { ...state.entities };
          const currentIds = [...state.ids];
          
          // Filter out active trades
          const nonActiveIds = currentIds.filter(id => 
            currentEntities[id] && currentEntities[id].status !== 'ACTIVE'
          );
          
          // Set the initial state to just the non-active trades
          tradesAdapter.setAll(
            state, 
            nonActiveIds.map(id => currentEntities[id])
          );
          
          // Now add the newly fetched active trades
          tradesAdapter.upsertMany(state, payload);
        } else {
          // For other statuses, just upsert the trades
          tradesAdapter.upsertMany(state, payload);
        }
        
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchTrades.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Handle closed trades pagination
      .addCase(fetchClosedTrades.pending, (state) => {
        state.closedTradesLoading = true;
        state.error = null;
      })
      .addCase(fetchClosedTrades.fulfilled, (state, action) => {
        // For paginated responses, update state with pagination info
        if (action.payload.results) {
          tradesAdapter.setMany(state, action.payload.results);
          
          // Properly calculate current page from URL or use the requested page
          let currentPage = 1;
          if (action.payload.next) {
            const nextPage = parseInt(new URLSearchParams(action.payload.next.split('?')[1] || '').get('page'));
            currentPage = nextPage - 1;
          } else if (action.payload.previous) {
            const prevPage = parseInt(new URLSearchParams(action.payload.previous.split('?')[1] || '').get('page'));
            currentPage = prevPage + 1;
          }
          
          // If we can't determine the page from URLs, use meta.arg
          if (isNaN(currentPage) && action.meta && action.meta.arg) {
            currentPage = action.meta.arg.page || 1;
          }
          
          // Calculate page size from request or response
          const pageSize = action.meta?.arg?.pageSize || 
                           parseInt(new URLSearchParams(action.payload.next?.split('?')[1] || '').get('page_size')) || 
                           10;
          
          // Calculate total pages
          const totalPages = Math.ceil(action.payload.count / pageSize) || 1;
          
          state.pagination = {
            count: action.payload.count,
            next: action.payload.next,
            previous: action.payload.previous,
            page: currentPage,
            pageSize: pageSize,
            totalPages: totalPages
          };
        } else {
          // Fallback for non-paginated responses
          tradesAdapter.setMany(state, action.payload);
        }
        state.closedTradesLoading = false;
        state.error = null;
      })
      .addCase(fetchClosedTrades.rejected, (state, action) => {
        state.closedTradesLoading = false;
        state.error = action.error.message;
      });
  },
});

// Selectors
export const {
  selectById: selectTradeById,
  selectAll: selectAllTrades,
} = tradesAdapter.getSelectors((state) => state.trade);

// Custom selector to select active trades
export const selectActiveTrades = (state) => {
  return selectAllTrades(state).filter(trade => trade.status === 'ACTIVE');
};

// Custom selector to select closed trades
export const selectClosedTrades = (state) => {
  return selectAllTrades(state).filter(trade => trade.status === 'CLOSED');
};

// Pagination selectors
export const selectTradePagination = (state) => state.trade.pagination;
export const selectTradeLoading = (state) => state.trade.loading;
export const selectClosedTradesLoading = (state) => state.trade.closedTradesLoading;

export default tradeSlice.reducer;